import { useState } from "react";
import { FileCheck, Play, Loader2, CheckCircle, Clock, AlertCircle } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { useTenant } from "@/hooks/useTenant";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

export default function TaxTracker() {
  const { orgId, familyId } = useTenant();
  const queryClient = useQueryClient();
  const [generating, setGenerating] = useState(false);
  const [running, setRunning] = useState(false);
  const [taxYear, setTaxYear] = useState(String(new Date().getFullYear() - 1));

  // Fetch K-1 doc_expected records
  const { data: k1Expectations, isLoading } = useQuery({
    queryKey: ["tax-tracker-k1s", orgId, familyId, taxYear],
    queryFn: async () => {
      if (!orgId) return [];

      const periodEnd = `${taxYear}-12-31`;
      let query = supabase
        .from("doc_expected")
        .select(`
          *,
          counterparties (name),
          doc_requirements (doc_type, cadence)
        `)
        .eq("org_id", orgId)
        .eq("period_end", periodEnd);

      if (familyId) query = query.eq("family_id", familyId);

      const { data, error } = await query.order("due_at", { ascending: true });
      if (error) throw error;
      return (data || []).filter(d => d.doc_requirements?.doc_type === "k1");
    },
    enabled: !!orgId,
  });

  const received = k1Expectations?.filter(e => e.status === "received").length || 0;
  const total = k1Expectations?.length || 0;
  const outstanding = total - received;
  const completionPct = total > 0 ? Math.round((received / total) * 100) : 0;

  const generateExpectations = async () => {
    setGenerating(true);
    try {
      const { data, error } = await supabase.functions.invoke("run-tax-tracker", {
        body: { orgId, generateExpectations: true, taxYear: parseInt(taxYear), runChase: false },
      });
      if (error) throw error;
      toast.success(`Generated ${data?.expectationsCreated || 0} K-1 expectations (${data?.expectationsSkipped || 0} already existed)`);
      queryClient.invalidateQueries({ queryKey: ["tax-tracker-k1s"] });
    } catch (err) {
      toast.error("Failed to generate expectations: " + (err instanceof Error ? err.message : "Unknown error"));
    } finally {
      setGenerating(false);
    }
  };

  const runChaseNow = async () => {
    setRunning(true);
    try {
      const { data, error } = await supabase.functions.invoke("run-tax-tracker", {
        body: { orgId, generateExpectations: false, taxYear: parseInt(taxYear), runChase: true },
      });
      if (error) throw error;
      toast.success(`Chase complete: ${data?.chasesCreated || 0} chase(s) created`);
      queryClient.invalidateQueries({ queryKey: ["tax-tracker-k1s"] });
    } catch (err) {
      toast.error("Failed to run chase: " + (err instanceof Error ? err.message : "Unknown error"));
    } finally {
      setRunning(false);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "received": return <Badge variant="default"><CheckCircle className="mr-1 h-3 w-3" />Received</Badge>;
      case "expected": return <Badge variant="secondary"><Clock className="mr-1 h-3 w-3" />Expected</Badge>;
      case "late": return <Badge variant="destructive"><AlertCircle className="mr-1 h-3 w-3" />Late</Badge>;
      case "chasing": return <Badge variant="destructive"><AlertCircle className="mr-1 h-3 w-3" />Chasing</Badge>;
      default: return <Badge variant="outline">{status}</Badge>;
    }
  };

  if (isLoading) return <div className="flex-1 p-8">Loading...</div>;

  return (
    <div className="flex-1 space-y-6 px-4 py-6">

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">K-1 Tax Document Tracker</h1>
          <p className="text-muted-foreground">Track K-1 tax documents across all fund managers</p>
        </div>
        <div className="flex gap-2 items-center">
          <div className="flex items-center gap-2">
            <Label htmlFor="taxYear" className="text-sm">Tax Year:</Label>
            <Input
              id="taxYear"
              type="number"
              className="w-24"
              value={taxYear}
              onChange={(e) => setTaxYear(e.target.value)}
            />
          </div>
          <Button variant="outline" onClick={generateExpectations} disabled={generating}>
            {generating ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <FileCheck className="mr-2 h-4 w-4" />}
            Generate Season
          </Button>
          <Button onClick={runChaseNow} disabled={running || outstanding === 0}>
            {running ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Play className="mr-2 h-4 w-4" />}
            Run Chase
          </Button>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="pb-3"><CardTitle className="text-sm font-medium">Expected K-1s</CardTitle></CardHeader>
          <CardContent><div className="text-2xl font-bold">{total}</div></CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-3"><CardTitle className="text-sm font-medium">Received</CardTitle></CardHeader>
          <CardContent><div className="text-2xl font-bold text-green-600">{received}</div></CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-3"><CardTitle className="text-sm font-medium">Outstanding</CardTitle></CardHeader>
          <CardContent><div className="text-2xl font-bold text-amber-600">{outstanding}</div></CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-3"><CardTitle className="text-sm font-medium">Completion</CardTitle></CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{completionPct}%</div>
            <Progress value={completionPct} className="mt-2" />
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>K-1 Status Matrix — Tax Year {taxYear}</CardTitle>
          <CardDescription>Track K-1 receipt status by fund manager</CardDescription>
        </CardHeader>
        <CardContent>
          {k1Expectations && k1Expectations.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Fund Manager</TableHead>
                  <TableHead>Due Date</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Last Activity</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {k1Expectations.map((exp) => (
                  <TableRow key={exp.id}>
                    <TableCell className="font-medium">{exp.counterparties?.name || "Unknown"}</TableCell>
                    <TableCell>{new Date(exp.due_at).toLocaleDateString()}</TableCell>
                    <TableCell>{getStatusBadge(exp.status)}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {exp.last_activity ? new Date(exp.last_activity).toLocaleDateString() : "—"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <FileCheck className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p>No K-1 expectations for tax year {taxYear}.</p>
              <p className="text-sm mt-2">Click "Generate Season" to create expectations from your doc requirements.</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
